"use client";

/**
 * UserContext — the signed-in identity, loaded from the Central Server via
 * GET /api/me (which proxies to /auth/me and transparently refreshes an
 * expired access token).
 *
 * The compact SessionUser from the Server is mapped to the richer ServerUser
 * the UI renders. Avatar / bio / frame come from GET /api/social/me (auth/me
 * does not carry them) and can be patched after a studio save.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import type { ServerUser, SessionUser } from "@/types/server";
import { mapSessionToServerUser } from "@/lib/session";
import { fetchMe } from "@/features/auth/api/authClient";
import { getMyProfile } from "@/features/connections/api/connectionsClient";

type IdentityStatus = "loading" | "unprovisioned" | "ready";

type ProfileOverlay = Partial<
  Pick<ServerUser, "avatarId" | "avatarBorderId" | "bio" | "connectionsCount">
>;

interface UserContextValue {
  user: ServerUser | null;
  session: SessionUser | null;
  status: IdentityStatus;
  refresh: () => Promise<void>;
  applyProfile: (patch: ProfileOverlay) => void;
}

const UserContext = createContext<UserContextValue | null>(null);

function isAuthRoute(pathname: string): boolean {
  return pathname.startsWith("/login") || pathname.startsWith("/auth");
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [session, setSession] = useState<SessionUser | null>(null);
  const [overlay, setOverlay] = useState<ProfileOverlay>({});
  const [status, setStatus] = useState<IdentityStatus>("loading");

  const applyProfile = useCallback((patch: ProfileOverlay) => {
    setOverlay((current) => ({ ...current, ...patch }));
  }, []);

  const refresh = useCallback(async () => {
    if (isAuthRoute(pathname)) {
      setSession(null);
      setOverlay({});
      setStatus("unprovisioned");
      return;
    }
    setStatus("loading");
    const me = await fetchMe();
    setSession(me);
    if (!me) {
      setOverlay({});
      setStatus("unprovisioned");
      return;
    }
    setStatus("ready");
    try {
      const profile = await getMyProfile();
      setOverlay({
        avatarId: profile.avatarKey ?? "",
        avatarBorderId: profile.avatarBorderKey,
        bio: profile.bio ?? "",
        connectionsCount: profile.friendCount,
      });
    } catch {
      // Session is still usable; avatar stays empty until social/me succeeds.
    }
  }, [pathname]);

  useEffect(() => {
    // Loads identity on mount / route change; the sync prefix of refresh()
    // (loading + auth-route reset) is intentional and hydration-safe.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const value = useMemo<UserContextValue>(
    () => ({
      user: session
        ? { ...mapSessionToServerUser(session), ...overlay }
        : null,
      session,
      status,
      refresh,
      applyProfile,
    }),
    [session, overlay, status, refresh, applyProfile],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within UserProvider");
  }
  return context;
}
